import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";

/**
 * Non-buyable placeholder that mirrors LookCard's structure exactly — same
 * aspect-[3/4] image, same pt-4 pb-2 caption block — so it sits flush in the
 * same grid row. The image shows the stored hero_image_url, heavily blurred
 * and softened with a warm cream overlay so it reads as a teaser. The only
 * text inside the image is a single "Get first access →" line at the bottom.
 * Clicking anywhere scrolls (or navigates then scrolls) to #newsletter.
 */
export default function ComingSoonTile({ nextNumber = "08" }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [heroImage, setHeroImage] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setHeroImage(data?.hero_image_url || "");
    }).catch(() => {});
  }, []);

  const goToNewsletter = () => {
    if (loc.pathname === "/") {
      const el = document.getElementById("newsletter");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      nav("/#newsletter");
    }
  };

  return (
    <button
      type="button"
      onClick={goToNewsletter}
      data-testid="coming-soon-tile"
      aria-label={`Look ${nextNumber} — coming soon. Join newsletter for first access.`}
      className="group block w-full text-left focus:outline-none"
    >
      {/* IMAGE AREA — same aspect-[3/4] as a real LookCard */}
      <div className="relative overflow-hidden aspect-[3/4]" style={{ background: "#EBE5ED" }}>
        {heroImage ? (
          <img
            src={resolveImage(heroImage)}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ filter: "blur(14px) saturate(0.8)", transform: "scale(1.1)" }}
          />
        ) : null}
        {/* warm cream overlay — hides details, feels like a hidden preview, keeps palette consistent across any hero image */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(253,251,247,0.65) 0%, rgba(253,251,247,0.78) 60%, rgba(235,229,237,0.84) 100%)" }} />
        {/* one small line at the bottom — the only text inside the image */}
        <div className="absolute inset-x-0 bottom-4 flex justify-center">
          <span className="text-[10px] uppercase tracking-editorial px-3 py-1 border-b transition-colors group-hover:text-[#4A464A]" style={{ color: "#4A464A", borderColor: "#4A464A" }}>
            Get first access →
          </span>
        </div>
      </div>

      {/* CAPTION — identical layout to LookCard, no heart */}
      <div className="pt-4 pb-2">
        <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5]">LOOK {nextNumber}</div>
        <div className="font-serif-display text-[19px] md:text-[20px] mt-1">Coming Soon</div>
        <div className="text-[12px] mt-1 text-[#4A464A]/70 line-clamp-1">A new look is on its way.</div>
        <div className="text-[13px] mt-2 tracking-editorial"><em className="not-italic italic text-[#C3B1C5]">dropping soon</em></div>
      </div>
    </button>
  );
}
